import * as React from "react";
import dayjs from "dayjs";

import { Box, Grid, ToggleButton, ToggleButtonGroup } from "@mui/material";

import CustomInput from "../../../../helper-components/input/CustomInput";
import CustomButton from "../../../../helper-components/input/CustomButton";

import CustomSelection from "../../../../helper-components/form/CustomSelection";

import { validateRow } from "../../../../helper-functions/validation/objectValidation";
import { querySelectResources } from "../../../../helper-functions/api/resourceQueries";
import { addFormToObj, getEllipsis, setInsertObjectState, setValue } from "../../../../helper-functions/shared/miscUtils";
import { TEXT_TRANSLATION_VALIDATION, IS_TEXT_TRANSLATION_VALID } from "../../../../helper-functions/validation/translationValidation";

import { getLanguage } from "../../../../config/language/language";
import { TEXT_TRANSLATION_OBJECT, TRANSLATE_LANGUAGES } from "../../../../config/constants/Constants";

/**
 * A form to update a text translation.
 */
export default function TextTranslationEditForm(props) {
    // Because modals don't re-render if the props' state changes, use a callback.
    const { callback, row: existingRow = {} } = props;

    const object = TEXT_TRANSLATION_OBJECT;
    const validation = TEXT_TRANSLATION_VALIDATION;
    const isValid = IS_TEXT_TRANSLATION_VALID;

    const [row, setRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(object))));
    const [showOriginal, setShowOriginal] = React.useState(false);

    const hasOriginal = Boolean(existingRow.original_text);
    const textRow = showOriginal && hasOriginal ? { ...row, text: existingRow.original_text } : row;

    // Initialize the temporary row object with existing values.
    React.useEffect(() => {
        const newRow = {
            ...row,
            ...existingRow,

            _show_accordion: false,
            _instructions: existingRow.meta_data?.instructions ?? "",

            // Convert to dayjs format if datetime exists.
            created_on: existingRow.created_on ? dayjs(existingRow.created_on) : null,
            updated_on: existingRow.updated_on ? dayjs(existingRow.updated_on) : null,

            form: {
                ...row.form,
                ...existingRow.form,
                saved: false // Required to allow editing input fields.
            }
        };

        setInsertObjectState(setRow, newRow);
    }, []);

    /**
     * Executed when typing something into an input field.
     */
    const onChange = (obj, key, value) => {
        const newRow = JSON.parse(JSON.stringify(row));
        setValue(newRow, key, value);

        if (key === "_show_accordion") {
            if (!value) newRow.resource_id = existingRow.resource_id; // Reset resource selection if the accordion is closed.

            if (value) props.showSnackbar({ message: "Resource selection reset", color: "info" });
        }

        if (key === "_instructions") {
            if (!newRow.meta_data) newRow.meta_data = {};
            newRow.meta_data.instructions = value;
        }

        validateRow(obj, validation, newRow);
        setInsertObjectState(setRow, newRow);
    };

    const handleClick = () => {
        if (!isValid(row)) {
            props.showSnackbar({ message: getLanguage().SNACKBAR_ERROR_FORM_INVALID, color: "error" });
            return;
        }

        const options = { row };
        callback(true, options);
    };

    const handleResource = (selected = []) => {
        const ids = selected.map((el) => el.id);

        onChange(row, "resource_id", ids?.[0]);
    };

    return (
        <Box className="box">
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <CustomInput name="language" row={row} onChange={onChange} label="Translate into language" select={TRANSLATE_LANGUAGES} />
                </Grid>

                <Grid item xs={12} style={{ whiteSpace: "pre-wrap" }}>
                    {hasOriginal && (
                        <Box display="flex" justifyContent="center" mb={1}>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={showOriginal ? "original" : "translated"}
                                onChange={(event, value) => {
                                    if (!value) return;
                                    setShowOriginal(value === "original");
                                }}>
                                <ToggleButton value="translated">Translated</ToggleButton>
                                <ToggleButton value="original">Original</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    )}
                    <CustomInput
                        name="text"
                        row={textRow}
                        onChange={onChange}
                        label={showOriginal ? "Original transcript" : "Translated text"}
                        disabled={showOriginal}
                        multiline
                        minRows={2}
                        maxRows={8}
                    />
                </Grid>

                <Grid item xs={12}>
                    <CustomInput
                        name="_instructions"
                        row={row}
                        onChange={onChange}
                        label="Additional instructions"
                        multiline
                        minRows={2}
                        maxRows={4}
                        placeholder="Remove sponsored segments and recommended resources"
                    />
                </Grid>

                <Grid item xs={12} style={{ textAlign: "center" }}>
                    <CustomInput name="_show_accordion" row={row} onChange={onChange} label="Select another resource" switch />
                </Grid>

                {/* Show accordion only if the switch is enabled. */}
                {row._show_accordion && (
                    <Grid item xs={12}>
                        <CustomSelection
                            {...props}
                            callback={handleResource}
                            query={querySelectResources}
                            getPrimarySearchText={(el) => el?.name}
                            getSecondarySearchText={(el) => el?.description}
                            getPrimaryText={(el) => getEllipsis(el?.name, 50)}
                            getSecondaryText={(el) => getEllipsis(el?.description, 50)}
                        />
                    </Grid>
                )}
            </Grid>

            <CustomButton {...props} title={"Update text translation"} onClick={handleClick} className="button" fullWidth />
        </Box>
    );
}
